import * as React from 'react';
import PropTypes from 'prop-types';
import { KeyboardTimePicker } from '@material-ui/pickers';
import classnames from 'classnames';
import { makeStyles, Typography } from '@material-ui/core';
import { useSelector } from 'react-redux';
import { getTranslate } from 'react-localize-redux';

import { TimeIcon } from '../icons';
import { today } from '../../youwell-common/dateUtils';

const useStyles = makeStyles(theme => ({
    root: {
        width: 112,
        flexShrink: 0,

        '&$error': {
            border: theme.youwell.borders.error,
        },
    },
    error: {
    },
    input: {
        paddingLeft: theme.spacing(1),
    },
    iconButton: {
        padding: theme.spacing(1),
    },
    validationMessage: {
        color: theme.palette.error.main,
        marginTop: theme.spacing(1),
    },
}), { name: 'TimePicker' });

const TimePicker = ({
    value, onChange, className, validation, disabled, defaultValue, inputAdornmentBeforeInput, disableUnderline, validateWithoutValueChange, validationMessage,
}) => {
    const classes = useStyles();
    const translate = useSelector(state => getTranslate(state.localize));
    const [validationError, setValidationError] = React.useState(false);
    const [invalidDate, setInvalidDate] = React.useState(false);

    React.useEffect(() => {
        if (validateWithoutValueChange) {
            if (!validation || validation(value)) {
                setValidationError(false);
            } else {
                setValidationError(true);
            }
        }
    }, [validateWithoutValueChange, validation, value, validationMessage]);

    const validateAndUpdate = (date) => {
        setInvalidDate(false);
        if (date === null || !validation || validation(date)) {
            setValidationError(false);
            onChange(date);
        } else {
            setValidationError(true);
            onChange(date);
        }
    }

    const onValueChange = (d, val) => {
        if (d === null) {
            validateAndUpdate(d);
        } else if (d?.getTime && !Number.isNaN(d.getTime())) {
            validateAndUpdate(d);
        } else if (val === '24:00') {
            const midnight = new Date();
            midnight.setHours(0, 0, 0);
            validateAndUpdate(midnight);
        } else if (val?.[4] !== '_') {
            validateAndUpdate(null);
            setInvalidDate(true);
        }
    };

    const initialDate = today();
    if (defaultValue?.length === 5) {
        initialDate.setHours(Number(defaultValue.substr(0, 2)), Number(defaultValue.substr(3, 2)));
    }

    return (
        <>
            <KeyboardTimePicker
                className={classnames(className, classes.root, { [classes.error]: validationError || invalidDate })}
                value={value || null}
                onChange={onValueChange}
                autoOk
                clearable
                ampm={false}
                keyboardIcon={<TimeIcon className={classes.icon} />}
                InputAdornmentProps={{ position: inputAdornmentBeforeInput ? 'start' : 'end' }}
                minutesStep={5}
                KeyboardButtonProps={{
                    'aria-label': translate('time.setTime'),
                    className: classes.iconButton,
                }}
                placeholder={defaultValue || '00:00'}
                initialFocusedDate={initialDate}
                InputProps={{ className: classes.input, disableUnderline }}
                invalidDateMessage=" " // no message, but show red line
                disabled={disabled}
                clearLabel={translate('clear')}
                cancelLabel={translate('cancel')}
                okLabel={translate('ok')}
            />

            {validationError && validationMessage && (<Typography className={classes.validationMessage} variant="body2">{validationMessage}</Typography>)}

        </>
    );
};

TimePicker.propTypes = {
    onChange: PropTypes.func.isRequired,
    value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.object,
    ]),
    className: PropTypes.string,
    validation: PropTypes.func,
    disabled: PropTypes.bool,
    defaultValue: PropTypes.string,
    inputAdornmentBeforeInput: PropTypes.bool,
    disableUnderline: PropTypes.bool,
    validateWithoutValueChange: PropTypes.bool,
    validationMessage: PropTypes.string,
};

TimePicker.defaultProps = {
    value: null,
    className: null,
    validation: null,
    validationMessage: null,
    disabled: false,
    defaultValue: null,
    inputAdornmentBeforeInput: false,
    disableUnderline: false,
    validateWithoutValueChange: false,
};

export default TimePicker;
